<script lang="ts">
  import { xhr, formatDate } from "$app/utils"
  import { toast } from "$app/utils"
  import Honeybadger from "$app/utils/honeybadger"
  import {
    Loader,
    Pen,
    Trash,
    ChevronRight,
    ChevronLeft,
    ChevronDown
  } from "lucide-svelte"

  import { appStore } from "$app/store.svelte"

  type UserTypeExt = UserType & {
    edit?: boolean
    deleted?: boolean
    showDetail?: boolean
    onboarding_percent?: number
  }

  interface Props {
    user: UserTypeExt
  }

  let { user = $bindable() }: Props = $props()
  const usersList: { [key: number]: UserType } = appStore.users || {}
  const invitedBy: string =
    (usersList[user.invited_by_id] && usersList[user.invited_by_id]["name"]) ||
    ""

  let detailLoaded = $state(false)

  let promiseResend = $state(Promise.resolve())

  function handleResend() {
    promiseResend = resendInvitation()
  }

  async function resendInvitation() {
    try {
      const resp = await xhr.patch(
        `/plans/${appStore.plan.id}/settings/members/${user.id}/reinvite_user`
      )
      dispatch("Resend invitation", user)
      toast("The invitation has been resent.", "success")
    } catch (e) {
      Honeybadger.notify(e)
      toast("There was an error resending the invitation.", "error")
    }
  }

  let resettingPassword = $state(false)
  async function resetPassword() {
    resettingPassword = true
    try {
      await xhr.patch(
        `/plans/${appStore.plan.id}/settings/members/${user.id}/reset_password`
      )
      toast(
        `User instructions have been sent to ${user.name} ${user.email} to reset their password.`,
        "success"
      )
    } catch (e) {
      Honeybadger.notify(e)
      toast("There was an error resetting the password.", "error")
    } finally {
      resettingPassword = false
    }
  }

  let generatingPassword = false
  let newPass = $state("")
  async function generatePassword() {
    resettingPassword = true
    try {
      const resp = await xhr.patch(
        `/plans/${appStore.plan.id}/settings/members/${user.id}/generate_password`
      )
      toast(
        `A new password has been generated for ${user.name} ${user.email} and has been sent to their email.`,
        "success"
      )
      newPass = resp.data.password
    } catch (e) {
      Honeybadger.notify(e)
      toast("There was an error regenerating the password.", "error")
    } finally {
      resettingPassword = false
    }
  }

  let course_progresses: any[] = $state([])
  async function loadUserProgress(): Promise<void> {
    try {
      const resp = await xhr.get(
        `/plans/${appStore.plan.id}/settings/members/${user.id}/course_progresses`
      )

      course_progresses = resp.data
      detailLoaded = true
    } catch (e) {
      Honeybadger.notify(e)
      toast("There was an error loading the user progress.", "error")
    }
  }

  $effect(() => {
    if (!detailLoaded) {
      loadUserProgress()
    }
  })
</script>

<!-- svelte-ignore a11y_click_events_have_key_events -->
<div
  class="member-row cursor-pointer"
  onclick={() => (user.showDetail = !user.showDetail)}
  role="button"
  tabindex="0"
>
  <div class="grid grid-cols-12">
    <div class="px-6 py-3 whitespace-nowrap col-span-3">
      <div class="flex items-center">
        <button class="text-gray-500" data-name="more">
          {#if user.showDetail}
            <ChevronDown />
          {:else}
            <ChevronRight />
          {/if}
        </button>
        <div
          class="ml-4 text-sm font-medium text-gray-900 text-ellipsis overflow-hidden"
        >
          {user.name}
        </div>
      </div>
    </div>
    <div class="px-6 py-3 col-span-4">
      <div class="text-sm text-gray-900 text-ellipsis overflow-hidden w-56">
        {user.email}
      </div>
    </div>
    <div class="px-6 py-3 whitespace-nowrap col-span-3">
      <span
        class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800"
      >
        {appStore.user_roles[user.role]}
      </span>
    </div>
    <div class="px-6 py-3 whitespace-nowrap text-right col-span-2">
      <button
        class="btn btn-link"
        title="Edit"
        onclick={stopPropagation(() => dispatch("edit"))}
      >
        <Pen />
      </button>
    </div>
  </div>

  <div class:hidden={!user.showDetail}>
    <div class="px-6">
      <div
        class="grid grid-cols-12 border border-gray-200 rounded-lg text-sm px-4 py-2"
      >
        <div class="col-span-12">
          <div class="text-gray-700 font-semibold text-center">
            User Details
          </div>
        </div>
        <div class="col-span-6 grid grid-cols-2">
          <div class="text-gray-400 font-semibold text-right pr-1">Title:</div>
          <div>
            {#if user.title}
              {user.title}
            {:else}
              <span class="text-gray-500">Not set</span>
            {/if}
          </div>
        </div>

        <div class="col-span-6 grid grid-cols-2">
          <div class="text-gray-400 font-semibold text-right pr-1">
            Mobile Number:
          </div>
          <div>
            {#if user.mobile_number}
              {user.mobile_number}
            {:else}
              <span class="text-gray-500">Not set</span>
            {/if}
          </div>
        </div>

        <div class="col-span-6 grid grid-cols-2">
          <div class="text-gray-400 font-semibold text-right pr-1">
            Birthday:
          </div>
          <div>
            {#if user.birthdate}
              {formatDate(user.birthdate)}
            {:else}
              <span class="text-gray-500">Not set</span>
            {/if}
          </div>
        </div>

        <div class="col-span-6 grid grid-cols-2">
          <div class="text-gray-400 font-semibold text-right pr-1">
            Work Anniversary:
          </div>
          <div>
            {#if user.work_anniversary}
              {formatDate(user.work_anniversary)}
            {:else}
              <span class="text-gray-500">Not set</span>
            {/if}
          </div>
        </div>

        <div class="col-span-6 grid grid-cols-2">
          <div class="text-gray-400 font-semibold text-right pr-1">
            Invited By:
          </div>
          <div>
            {#if invitedBy}
              {invitedBy}
            {:else}
              <span class="text-gray-500">Not invited</span>
            {/if}
          </div>
        </div>

        <div class="col-span-6 grid grid-cols-2">
          <div class="text-gray-400 font-semibold text-right pr-1">
            Invite Accepted:
          </div>
          <div>
            {#if user.invitation_accepted_at}
              {formatDate(user.invitation_accepted_at)}
            {:else}
              <span class="text-gray-500">Pending acceptance</span>
            {/if}
          </div>
        </div>

        <div class="col-span-6 grid grid-cols-2">
          <div class="text-gray-400 font-semibold text-right pr-1">
            Number of Logins:
          </div>
          {#if user.sign_in_count}
            {user.sign_in_count}
          {:else}
            <span class="text-gray-400">Member has never logged in</span>
          {/if}
        </div>

        <div class="col-span-6 grid grid-cols-2">
          <div class="text-gray-400 font-semibold text-right pr-1">
            Last Access Date:
          </div>
          {#if user.last_sign_in_at}
            <span>{formatDate(user.last_sign_in_at)}</span>
          {:else}
            <span class="text-gray-400">Member has never logged in</span>
          {/if}
        </div>

        <div class="col-span-6 grid grid-cols-2">
          <div class="text-gray-400 font-semibold text-right pr-1">
            Display Name:
          </div>
          {#if user.display_name}
            <span>{user.display_name}</span>
          {:else}
            <span class="text-gray-400">Display name not set</span>
          {/if}
        </div>

        <div class="col-span-6 grid grid-cols-2">
          <div class="text-gray-400 font-semibold text-right pr-1">
            Onboarding Percent:
          </div>

          <span class="font-semibold">{user.onboarding_percent || 0}%</span>
        </div>
      </div>
    </div>

    <div class="px-6 py-2 grid grid-cols-2 gap-4">
      <div class="mt-4 py-2 px-4 border border-gray-200 rounded-lg">
        <div class="text-gray-500 font-semibold text-center">
          Course progress
        </div>

        <table class="text-sm mt-2">
          <tbody>
            {#each course_progresses as cp}
              <tr
                class={[
                  "",
                  cp.progress === 100 ? "text-green-700" : "",
                  cp.progress > 0 && cp.progress < 100 && "text-yellow-800",
                  cp.progress === 0 && "text-gray-500"
                ]}
              >
                <td>{cp.course_name}</td>
                <td class="font-semibold px-4">
                  {cp.progress}%
                </td>
                <td>
                  <span class="progress-color"></span>
                </td>
              </tr>
            {:else}
              <tr>
                <td colspan="2">
                  <div class="py-2 px-3">
                    {#if detailLoaded}
                      <p class="text-gray-700 text-sm">No course progresses</p>
                    {:else}
                      <Loader class="rotate" />
                    {/if}
                  </div>
                </td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>

      <div
        class="mt-4 border border-gray-200 rounded-lg px-4 py-2 flex flex-col items-center"
      >
        <div class="text-gray-500 font-semibold">Manage Password</div>
        {#if user.invitation_accepted_at}
          <div class="text-xs text-center text-gray-700 mt-4">
            This will send an email to the user for them to reset their
            password.
          </div>
          <button
            class="btn btn-outline mt-2"
            type="button"
            onclick={(e) => {
              e.stopPropagation()
              resetPassword()
            }}
            disabled={resettingPassword}
          >
            Send password reset email
          </button>

          <div class="text-center py-4">
            <div class="text-xs text-center text-gray-700 mt-2">
              This will generate a new random password for the user without
              their input.
            </div>
            <button
              class="btn btn-outline mt-2"
              type="button"
              onclick={(e) => {
                e.stopPropagation()
                generatePassword()
              }}
              disabled={generatingPassword}
            >
              Generate new password
            </button>
          </div>

          {#if newPass}
            <div class="text-center py-4">
              <div class="text-gray-400 font-semibold">New password:</div>
              <div class="font-semibold">{newPass}</div>
            </div>
          {/if}
        {:else}
          <div class="py-2 px-3">
            <p class="text-gray-600 text-sm">
              This member has not yet reponded to the invitation email. You can
              use the button below to resend their invitation if needed.
            </p>

            <div class="flex justify-center">
              <button
                class="btn btn-primary mt-3"
                type="button"
                onclick={(e) => {
                  e.stopPropagation()
                  handleResend()
                }}
                data-name="resend-invitation"
              >
                {#await promiseResend}
                  <Loader class="rotate" />
                {:then}
                  Resend invitation
                {/await}
              </button>
            </div>
          </div>
        {/if}
      </div>
    </div>
  </div>
</div>

<style lang="postcss">
  .progress-color {
    display: inline-block;
    width: 1rem;
    height: 1rem;
  }
  table .text-green-700 .progress-color {
    /* @apply bg-green-700; */
    background-color: var(--green-700);
  }
  table .text-yellow-800 .progress-color {
    /* @apply bg-yellow-500; */
    background-color: var(--yellow-500);
  }

  table .text-gray-500 .progress-color {
    /* @apply bg-gray-300; */
    background-color: var(--gray-300);
  }
</style>
